(ns ui.view.grid-opensearch.theme
  "The AG Grid theme of the new grid. It is the Quartz theme of AG Grid with the
   parameters of the ZapKit Table: the border and the radius of the table, the header
   and the body colors, the hairline between rows, no line between columns, no line
   or handle in the header, a quiet hover, and the checkbox of ZapKit. AG Grid draws
   its own DOM, so the classes of the generated `ui.components.table.zapkit-styles`
   cannot be used. The parameters use the CSS variables of the ZapKit tokens, so the
   theme follows the light and the dark token sets. `token-names` lists them, and a
   test checks them against the tokens of the generated Table styles.

   The row height and the header height come from `ui.view.grid-opensearch.density`."
  (:require
   ["ag-grid-community" :refer [themeQuartz]]
   [oops.core :as oops]
   [ui.view.grid-opensearch.density :as grid-density]))

(defn- hairline
  []
  {:style "solid"
   :width 1
   :color "var(--table-border-default)"})

(defn theme-params
  "The parameters of the theme for a density. `:show-entity-type?` gives the height of
   the header."
  [{:keys [density] :as options}]
  (let [density (grid-density/normalize-density density)]
    {:backgroundColor                 "var(--table-bg-default)"
     :foregroundColor                 "var(--table-cell-text)"
     :cellTextColor                   "var(--table-cell-text)"
     :borderColor                     "var(--table-border-default)"
     :wrapperBorder                   true
     :wrapperBorderRadius             "var(--radius-table)"
     :fontSize                        14
     :cellHorizontalPadding           "var(--spacing-table-cell-px)"
     :rowHeight                       (get grid-density/row-heights density)
     :rowBorder                       (hairline)
     :columnBorder                    false
     :rowHoverColor                   "var(--table-row-bg-hover)"
     :selectedRowBackgroundColor      "var(--table-row-bg-selected)"
     :pinnedColumnBorder              (hairline)
     :headerHeight                    (grid-density/header-height options)
     :headerBackgroundColor           "var(--table-header-bg)"
     :headerTextColor                 "var(--table-header-text)"
     :headerFontSize                  14
     :headerFontWeight                500
     :headerRowBorder                 (hairline)
     :headerColumnBorder              false
     :headerColumnResizeHandleColor   "transparent"
     :accentColor                     "var(--checkbox-bg-selected)"
     :checkboxBorderRadius            "var(--radius-checkbox)"
     :checkboxBorderWidth             1
     :checkboxUncheckedBackgroundColor "var(--checkbox-bg-default)"
     ;; The resting border uses the Checkbox hover token, not the default token.
     ;; The default token (gray-200 in light) is too faint for a selection
     ;; checkbox that sits alone on a plain row. The hover token gives more
     ;; contrast in both themes (gray-300 in light, gray-400 in dark) and stays
     ;; a Checkbox token.
     :checkboxUncheckedBorderColor    "var(--checkbox-border-hover)"
     :checkboxCheckedBackgroundColor  "var(--checkbox-bg-selected)"
     :checkboxCheckedBorderColor      "var(--checkbox-border-selected)"
     :checkboxCheckedShapeColor       "var(--checkbox-checkmark-bg)"}))

(defn token-names
  "The names of the CSS variables that the parameters use, without the `--`. Sorted."
  [params]
  (->> (tree-seq coll? seq params)
       (filter string?)
       (mapcat (fn [value]
                 (map second (re-seq #"var\(--([a-z0-9-]+)\)" value))))
       distinct
       sort
       vec))

(defonce ^:private themes*
  (atom {}))

(defn grid-theme
  "The AG Grid theme object for the options `:density` and `:show-entity-type?`. The
   object is made once for each pair of values, so AG Grid gets the same object while
   the options do not change."
  [{:keys [density show-entity-type?]}]
  (let [options {:density           (grid-density/normalize-density density)
                 :show-entity-type? (boolean show-entity-type?)}]
    (or (get @themes* options)
        (let [theme (oops/ocall themeQuartz "withParams" (clj->js (theme-params options)))]
          (swap! themes* assoc options theme)
          theme))))
